import { useState, useEffect } from 'react';
import { useSmartError } from '@/hooks/useSmartError';
import '@/components/SmartError';
import { PlybookLaminationView } from '@/components/Plybook/PlybookLaminationView';
import { LaminationStartFlow } from '@/components/Operator/LaminationStartFlow';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { MaterialConsumptionDialog } from '@/components/Materials';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Package,
  Plus,
  CheckCircle,
  Clock,
  Play,
  Pause,
  Building2,
  Calendar,
  User,
  Loader2,
  Lock,
  AlertTriangle,
  PlayCircle,
  BookOpenCheck,
  Layers,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface WorkOrder {
  id: string;
  order_number: string;
  product_name: string;
  quantity: number;
  priority: string;
  planning_status: string;
  scheduled_date: string;
  is_paused: boolean;
  paused_at: string;
  pause_reason: string;
  customer?: { company_name: string };
  items_completed: number;
  plybook_id?: string;
  mold_id?: string;
  linked_plotter_cut_id?: string;
  plybook?: { name: string; total_plies: number; plies?: any[] };
  items_total: number;
}

const PRIORITY_ORDER: Record<string, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  urgent: { label: 'URGENTE', color: 'bg-red-500' },
  high: { label: 'Alta', color: 'bg-orange-500' },
  normal: { label: 'Normal', color: 'bg-blue-500' },
  low: { label: 'Baja', color: 'bg-gray-500' },
};

export default function OperatorWorkView() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { handleError, activeCorrection, clearCorrection, onCorrectionSuccess, CorrectionDialog } = useSmartError();
  const [myOrders, setMyOrders] = useState<WorkOrder[]>([]);
  const [availableOrders, setAvailableOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [piecesInput, setPiecesInput] = useState(1);
  const [pauseReason, setPauseReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [plybookDialogOpen, setPlybookDialogOpen] = useState(false);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [selectedOrderForPlybook, setSelectedOrderForPlybook] = useState<WorkOrder | null>(null);

  // Estado único para el diálogo - si tiene orden, está abierto
  const [selectedOrderForNfc, setSelectedOrderForNfc] = useState<WorkOrder | null>(null);
  // Iniciar producción - abre flujo de laminación con escaneo NFC
  const handleStartProduction = (order: WorkOrder) => {
    // Abrir flujo de laminación - el diálogo se abre cuando selectedOrderForNfc tiene valor
    setSelectedOrderForNfc(order);
    console.log("Estado actualizado:", order.order_number);
  };

  // Iniciar producción directamente (después de validación NFC o sin molde)
  const startProductionDirectly = async (order: WorkOrder) => {
    setSubmitting(true);
    try {
      const { error } = await api
        .from("work_orders")
        .update({
          planning_status: "in_production",
          production_started_at: new Date().toISOString(),
        })
        .eq("id", order.id);
      if (error) throw error;
      toast({ title: "Producción iniciada", description: order.order_number });
      fetchMyOrders(); fetchAvailableOrders();
    } catch (err: any) {
      // Usar sistema de errores inteligentes
      handleError(err, {
        id: order.id,
        order_number: order.order_number,
        product_name: order.product_name,
        plybook_id: order.plybook_id,
        mold_id: order.mold_id,
      }, () => startProductionDirectly(order));
    } finally {
      setSubmitting(false);
    }
  };

  // Cuando se completa la validación NFC
  const handleNfcValidated = (encounterId: string, moldData: any) => {
    if (selectedOrderForNfc) {
      startProductionDirectly(selectedOrderForNfc);
    }
    setSelectedOrderForNfc(null);
  };

  // Cuando se salta la validación NFC (modo manual)
  const handleNfcSkip = () => {
    if (selectedOrderForNfc) {
      startProductionDirectly(selectedOrderForNfc);
    }
    setSelectedOrderForNfc(null);
  };

  const fetchMyOrders = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      const today = format(new Date(), 'yyyy-MM-dd');

      const { data, error } = await api
        .from('work_orders')
        .select('*')
        .eq('is_active', true)
        .eq('is_suspended', false)
        .eq('assigned_operator', user.id)
        .in('planning_status', ['scheduled', 'in_production', 'paused'])
                .or(["scheduled_date.eq." + format(new Date(), "yyyy-MM-dd"), "planning_status.eq.in_production", "planning_status.eq.paused"])
        .order('priority', { ascending: true });

      if (error) throw error;

      const customerIds = [...new Set((data || []).map((o: any) => o.customer_id).filter(Boolean))];
      const customersRes = customerIds.length > 0
        ? await api.from('customers').select('id,company_name').in('id', customerIds)
        : { data: [] };

      const customersMap = Object.fromEntries((customersRes.data || []).map((c: any) => [c.id, c]));
      // Cargar plybooks
      const plybookIds = [...new Set((data || []).map((o: any) => o.plybook_id).filter(Boolean))];
      let plybooksMap: Record<string, any> = {};
      let pliesMap: Record<string, any[]> = {};
      
      if (plybookIds.length > 0) {
        const plybooksRes = await api.from("plybooks").select("id, name, total_plies").in("id", plybookIds);
        plybooksMap = Object.fromEntries((plybooksRes.data || []).map((p: any) => [p.id, p]));
        
        const pliesRes = await api.from("plybook_plies").select("plybook_id, ply_number, material_name, fiber_angle").in("plybook_id", plybookIds).order("ply_number", { ascending: true });
        (pliesRes.data || []).forEach((ply: any) => {
          if (!pliesMap[ply.plybook_id]) pliesMap[ply.plybook_id] = [];
          pliesMap[ply.plybook_id].push(ply);
        });
      }


      const orderIds = (data || []).map((o: any) => o.id);
      const { data: itemsData } = orderIds.length > 0
        ? await api.from('work_order_items')
            .select('work_order_id, status')
            .in('work_order_id', orderIds)
        : { data: [] };

      const itemsStats: Record<string, { completed: number; total: number }> = {};
      (itemsData || []).forEach((item: any) => {
        if (!itemsStats[item.work_order_id]) {
          itemsStats[item.work_order_id] = { completed: 0, total: 0 };
        }
        itemsStats[item.work_order_id].total++;
        if (item.status === 'completed') {
          itemsStats[item.work_order_id].completed++;
        }
      });

      const enrichedData = (data || []).map((o: any) => ({
        ...o,
        customer: customersMap[o.customer_id],
        plybook: o.plybook_id && plybooksMap[o.plybook_id] ? { ...plybooksMap[o.plybook_id], plies: pliesMap[o.plybook_id] || [] } : undefined,
        items_completed: itemsStats[o.id]?.completed || 0,
        items_total: itemsStats[o.id]?.total || o.quantity,
      }));

      // Sort by priority
      enrichedData.sort((a: any, b: any) => {
        const prioA = PRIORITY_ORDER[a.priority] ?? 2;
        const prioB = PRIORITY_ORDER[b.priority] ?? 2;
        return prioA - prioB;
      });

      setMyOrders(enrichedData);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las órdenes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available orders (not assigned to anyone)
  const fetchAvailableOrders = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const { data, error } = await api
        .from('work_orders')
        .select('*')
        .eq('is_active', true)
        .eq('is_suspended', false)
        .is('assigned_operator', null)
        .eq('scheduled_date', today)
        .in('planning_status', ['scheduled', 'standby'])
        .order('priority', { ascending: true });
      if (error) throw error;
      setAvailableOrders(data || []);
    } catch (err) {
      console.error('Error fetching available orders:', err);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchMyOrders();
      fetchAvailableOrders();
      const interval = setInterval(() => {
        fetchMyOrders();
        fetchAvailableOrders();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  // Take an available order
  const handleTakeOrder = async (order: WorkOrder) => {
    if (!user?.id) return;
    try {
      const { error } = await api
        .from('work_orders')
        .update({ assigned_operator: user.id })
        .eq('id', order.id);
      if (error) throw error;
      toast({ title: 'OT asignada', description: order.order_number + ' ahora es tuya' });
      fetchMyOrders();
      fetchAvailableOrders();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleRegisterPieces = async () => {
    if (!selectedOrder || piecesInput <= 0) return;

    setSubmitting(true);
    try {
      const { data: pendingItems, error: itemsError } = await api
        .from('work_order_items')
        .select('id')
        .eq('work_order_id', selectedOrder.id)
        .eq('status', 'pending')
        .order('item_number')
        .limit(piecesInput);

      if (itemsError) throw itemsError;

      if (!pendingItems || pendingItems.length === 0) {
        toast({
          title: 'Sin piezas pendientes',
          description: 'No hay más piezas pendientes en esta OT',
          variant: 'destructive',
        });
        setSubmitting(false);
        return;
      }

      const itemIds = pendingItems.map((i: any) => i.id);
      const actualPieces = itemIds.length;

      const { error: updateError } = await api
        .from('work_order_items')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          assigned_operator: user?.id,
        })
        .in('id', itemIds);

      if (updateError) throw updateError;

      const { error: logError } = await api
        .from('operator_production_log')
        .insert({
          work_order_id: selectedOrder.id,
          operator_id: user?.id,
          pieces_completed: actualPieces,
          process_name: 'Producción',
          shift: getShift(),
        });

      if (logError) throw logError;

      toast({
        title: 'Piezas registradas',
        description: `${actualPieces} pieza${actualPieces > 1 ? 's' : ''} completada${actualPieces > 1 ? 's' : ''}`,
      });

      setRegisterDialogOpen(false);
      setSelectedOrder(null);
      setPiecesInput(1);
      fetchMyOrders(); fetchAvailableOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePause = async () => {
    if (!selectedOrder || !pauseReason.trim()) return;

    setSubmitting(true);
    try {
      // Update work order
      const { error: updateError } = await api
        .from('work_orders')
        .update({
          is_paused: true,
          paused_at: new Date().toISOString(),
          pause_reason: pauseReason,
          paused_by: user?.id,
          planning_status: 'paused',
        })
        .eq('id', selectedOrder.id);

      if (updateError) throw updateError;

      // Log pause
      const { error: logError } = await api
        .from('work_order_pauses')
        .insert({
          work_order_id: selectedOrder.id,
          operator_id: user?.id,
          reason: pauseReason,
        });

      if (logError) throw logError;

      toast({
        title: 'Trabajo pausado',
        description: 'La OT ha sido pausada correctamente',
      });

      setPauseDialogOpen(false);
      setSelectedOrder(null);
      setPauseReason('');
      fetchMyOrders(); fetchAvailableOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = async (order: WorkOrder) => {
    setSubmitting(true);
    try {
      // Calculate pause duration
      const pausedAt = new Date(order.paused_at);
      const now = new Date();
      const durationMinutes = Math.round((now.getTime() - pausedAt.getTime()) / 60000);

      // Update last pause record
      const { data: lastPause } = await api
        .from('work_order_pauses')
        .select('id')
        .eq('work_order_id', order.id)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1);

      if (lastPause && lastPause.length > 0) {
        await api
          .from('work_order_pauses')
          .update({
            ended_at: now.toISOString(),
            duration_minutes: durationMinutes,
          })
          .eq('id', lastPause[0].id);
      }

      // Resume work order
      const { error } = await api
        .from('work_orders')
        .update({
          is_paused: false,
          paused_at: null,
          pause_reason: null,
          paused_by: null,
          planning_status: 'in_production',
        })
        .eq('id', order.id);

      if (error) throw error;

      toast({
        title: 'Trabajo reanudado',
        description: `Pausado ${durationMinutes} minutos`,
      });

      fetchMyOrders(); fetchAvailableOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getShift = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 14) return 'mañana';
    if (hour >= 14 && hour < 22) return 'tarde';
    return 'noche';
  };

  const handleQuickAdd = (amount: number) => {
    if (!selectedOrder) return;
    const remaining = selectedOrder.items_total - selectedOrder.items_completed;
    setPiecesInput(Math.min(amount, remaining));
  };

  const openRegisterDialog = (order: WorkOrder) => {
    setSelectedOrder(order);
    setPiecesInput(1);
    setRegisterDialogOpen(true);
  };

  const openPauseDialog = (order: WorkOrder) => {
    setSelectedOrder(order);
    setPauseReason('');
    setPauseDialogOpen(true);
  };

  const openMaterialDialog = (order: WorkOrder) => {
    setSelectedOrder(order);
    setMaterialDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
        </div>

      {/* Diálogo de Plybook */}
      <Dialog open={plybookDialogOpen} onOpenChange={setPlybookDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <BookOpenCheck className="w-5 h-5" />
              Plybook: {selectedOrderForPlybook?.plybook?.name}
              <Badge variant="outline" className="ml-2">
                {selectedOrderForPlybook?.order_number}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(95vh-80px)]">
            {selectedOrderForPlybook && selectedOrderForPlybook.plybook_id && (
              <PlybookLaminationView
                workOrderId={selectedOrderForPlybook.id}
                plybookId={selectedOrderForPlybook.plybook_id}
                onComplete={() => {
                  setPlybookDialogOpen(false);
                  fetchMyOrders(); fetchAvailableOrders();
                }}
                onBack={() => setPlybookDialogOpen(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      </DashboardLayout>
    );
  }

  // Find first active (non-paused) order
  const activeOrderIndex = myOrders.findIndex(o => !o.is_paused && o.items_completed < o.items_total);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Module Tabs */}
        <ModuleTabs
          tabs={[
            { value: 'mitrabajo', label: 'Mi Trabajo', href: '/my-work' },
            { value: 'vistaoperario', label: 'Vista Operario', href: '/operator' },
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Mis Trabajos de Hoy
            </h1>
            <p className="text-muted-foreground mt-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}
            </p>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">
            <User className="w-4 h-4 mr-2" />
            {user?.email?.split('@')[0] || 'Operario'}
          </Badge>
        </div>

        {/* My Orders Section */}        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">          <User className="w-5 h-5" />          Mis OTs Asignadas ({myOrders.length})        </h2>        {/* Info about blocking */}
        {myOrders.length > 1 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Debes completar la primera OT antes de poder trabajar en las siguientes.
            </AlertDescription>
          </Alert>
        )}
{/* Available Orders Section */}        {availableOrders.length > 0 && (          <div className="mt-8">            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">              <Package className="w-5 h-5" />              OTs Disponibles ({availableOrders.length})            </h2>            <div className="space-y-3">              {availableOrders.map((order) => (                <Card key={order.id} className="p-4 border-dashed border-2 hover:border-primary/50 transition-all">                  <div className="flex items-center justify-between">                    <div>                      <Badge variant="outline" className="font-mono mb-1">{order.order_number}</Badge>                      <p className="font-medium">{order.product_name}</p>                      <p className="text-sm text-muted-foreground">{order.quantity} piezas</p>                    </div>                    <Button onClick={() => handleTakeOrder(order)}>                      <Plus className="w-4 h-4 mr-1" />                      Tomar                    </Button>                  </div>                </Card>              ))}            </div>          </div>        )}

        {/* Orders Grid */}
        {myOrders.length === 0 ? (
          <Card className="p-12 text-center">
            <Clock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              No tienes órdenes asignadas para hoy
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {myOrders.map((order, index) => {
              const progress = order.items_total > 0
                ? Math.round((order.items_completed / order.items_total) * 100)
                : 0;
              const isComplete = order.items_completed >= order.items_total;
              const isActive = index === activeOrderIndex;
              const isBlocked = !isActive && !order.is_paused && !isComplete;
              const priorityConfig = PRIORITY_LABELS[order.priority] || PRIORITY_LABELS.normal;

              return (
                <Card
                  key={order.id}
                  className={`p-6 transition-all ${
                    isBlocked ? 'opacity-50 bg-muted/50' :
                    order.is_paused ? 'border-amber-500 bg-amber-500/5' :
                    isComplete ? 'opacity-60' :
                    'hover:shadow-lg'
                  }`}
                >
                  <div className="flex items-start gap-6">
                    {/* Order Number */}
                    <div className="flex flex-col items-center gap-2">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${priorityConfig.color}`}>
                        {index + 1}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {priorityConfig.label}
                      </Badge>
                    </div>

                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="font-mono">
                              {order.order_number}
                            </Badge>
                            {order.is_paused && (
                              <Badge variant="outline" className="border-amber-500 text-amber-500">
                                <Pause className="w-3 h-3 mr-1" />
                                PAUSADA
                              </Badge>
                            )}
                            {isBlocked && (
                              <Badge variant="outline" className="border-gray-400 text-gray-400">
                                <Lock className="w-3 h-3 mr-1" />
                                BLOQUEADA
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-lg">{order.product_name}</h3>
                          {order.customer && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {order.customer.company_name}
                            </p>
                          )}
                        </div>
                        {isComplete ? (
                          <CheckCircle className="w-8 h-8 text-green-500" />
                        ) : isActive ? (
                          <Play className="w-8 h-8 text-primary" />
                        ) : order.is_paused ? (
                          <Pause className="w-8 h-8 text-amber-500" />
                        ) : (
                          <Lock className="w-8 h-8 text-gray-400" />
                        )}
                      </div>

                      {/* Pause info */}
                      {order.is_paused && order.paused_at && (
                        <Alert className="mb-3 bg-amber-500/10 border-amber-500/30">
                          <AlertDescription className="text-amber-700 dark:text-amber-400">
                            <strong>Pausada</strong> hace {formatDistanceToNow(new Date(order.paused_at), { locale: es })}
                            {order.pause_reason && <span> — {order.pause_reason}</span>}
                          </AlertDescription>
                        </Alert>
                      )}

{/* Plybook Info */}                      {order.plybook && (                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">                          <div className="flex items-center gap-2 mb-2">                            <BookOpenCheck className="w-4 h-4 text-blue-600" />                            <span className="font-medium text-blue-800 dark:text-blue-300">                              {order.plybook.name}                            </span>                            <Badge variant="outline" className="ml-auto">                              {order.plybook.total_plies} capas                            </Badge>                          </div>                          {order.plybook.plies && order.plybook.plies.length > 0 && (                            <div className="space-y-1 text-xs">                              {order.plybook.plies.map((ply: any) => (                                <div key={ply.ply_number} className="flex items-center gap-2 text-muted-foreground">                                  <Layers className="w-3 h-3" />                                  <span>Capa {ply.ply_number}:</span>                                  <span className="font-medium">{ply.material_name || "Sin material"}</span>                                  {ply.fiber_angle !== null && <span>({ply.fiber_angle}°)</span>}                                </div>                              ))}                            </div>                          )}                        </div>                      )}
                      {/* Progress */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Progreso</span>
                          <span className="font-semibold">
                            {order.items_completed} / {order.items_total} piezas
                          </span>
                        </div>
                        <Progress value={progress} className="h-3" />
                        <p className="text-right text-sm font-medium text-primary">
                          {progress}%
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        {order.is_paused ? (
                          <Button
                            className="flex-1 bg-amber-500 hover:bg-amber-600 text-lg py-6"
                            onClick={() => handleResume(order)}
                            disabled={submitting}
                          >
                            <PlayCircle className="w-5 h-5 mr-2" />
                            Reanudar Trabajo
                          </Button>
                        ) : isActive && order.planning_status === "scheduled" && !isComplete ? (
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700 text-lg py-6"
                            onClick={() => handleStartProduction(order)}
                            disabled={submitting}
                          >
                            <Play className="w-5 h-5 mr-2" />
                            Iniciar Producción
                          </Button>
                        ) : isActive && order.planning_status === "in_production" && !isComplete ? (
                          <>
                            <Button
                              className="flex-1 bg-gradient-magma hover:shadow-magma-hover text-lg py-6"
                              onClick={() => openRegisterDialog(order)}
                            >
                              <Plus className="w-5 h-5 mr-2" />
                              Registrar Piezas
                            </Button>
                            <Button
                              variant="outline"
                              className="py-6 border-amber-500 text-amber-500 hover:bg-amber-500/10"
                              onClick={() => openPauseDialog(order)}
                            >
                              <Pause className="w-5 h-5" />
                            </Button>
                          </>
                        ) : isComplete ? (
                          <div className="flex-1 text-center py-3 bg-green-500/10 rounded-lg">
                            <p className="text-green-600 font-medium">
                              <CheckCircle className="w-4 h-4 inline mr-2" />
                              Completada
                            </p>
                          </div>
                        ) : (
                          <div className="flex-1 text-center py-3 bg-muted rounded-lg">
                            <p className="text-muted-foreground font-medium">
                              <Lock className="w-4 h-4 inline mr-2" />
                              Completa la OT anterior primero
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
{/* Available Orders Section */}        {availableOrders.length > 0 && (          <div className="mt-8">            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">              <Package className="w-5 h-5" />              OTs Disponibles ({availableOrders.length})            </h2>            <div className="space-y-3">              {availableOrders.map((order) => (                <Card key={order.id} className="p-4 border-dashed border-2 hover:border-primary/50 transition-all">                  <div className="flex items-center justify-between">                    <div>                      <Badge variant="outline" className="font-mono mb-1">{order.order_number}</Badge>                      <p className="font-medium">{order.product_name}</p>                      <p className="text-sm text-muted-foreground">{order.quantity} piezas</p>                    </div>                    <Button onClick={() => handleTakeOrder(order)}>                      <Plus className="w-4 h-4 mr-1" />                      Tomar                    </Button>                  </div>                </Card>              ))}            </div>          </div>        )}
      </div>

      {/* Register Pieces Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Registrar Piezas Completadas</DialogTitle>
            {selectedOrder && (
              <DialogDescription>
                {selectedOrder.order_number} - {selectedOrder.product_name}
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between mb-2">
                  <span>Progreso actual:</span>
                  <span className="font-bold">
                    {selectedOrder.items_completed} / {selectedOrder.items_total}
                  </span>
                </div>
                <Progress
                  value={(selectedOrder.items_completed / selectedOrder.items_total) * 100}
                  className="h-2"
                />
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Añadir rápido:</p>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 5, 10, 20].map((num) => (
                    <Button
                      key={num}
                      variant={piecesInput === num ? 'default' : 'outline'}
                      className="text-lg py-6"
                      onClick={() => handleQuickAdd(num)}
                    >
                      +{num}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">O escribe cantidad:</p>
                <Input
                  type="number"
                  min={1}
                  max={selectedOrder.items_total - selectedOrder.items_completed}
                  value={piecesInput}
                  onChange={(e) => setPiecesInput(Math.max(1, parseInt(e.target.value) || 1))}
                  className="text-center text-2xl py-6"
                />
              </div>

              <div className="p-4 bg-primary/10 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Registrarás:</p>
                <p className="text-3xl font-bold text-primary">
                  {piecesInput} pieza{piecesInput > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRegisterDialogOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRegisterPieces}
              disabled={submitting || piecesInput <= 0}
              className="bg-gradient-magma"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pause Dialog */}
      <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Pause className="w-5 h-5 text-amber-500" />
              Pausar Trabajo
            </DialogTitle>
            {selectedOrder && (
              <DialogDescription>
                {selectedOrder.order_number} - {selectedOrder.product_name}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm font-medium mb-2">Razón de la pausa *</p>
              <Textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="Ej: Descanso, espera de material, problema técnico..."
                rows={3}
              />
            </div>

            <Alert className="bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                El supervisor podrá ver el tiempo de pausa y la razón.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setPauseDialogOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePause}
              disabled={submitting || !pauseReason.trim()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Pause className="w-4 h-4 mr-2" />
              )}
              Pausar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Material Consumption Dialog */}
      <MaterialConsumptionDialog
        open={materialDialogOpen}
        onOpenChange={setMaterialDialogOpen}
        workOrderId={selectedOrder?.id}
        workOrderNumber={selectedOrder?.order_number}
        onConsumed={() => {}}
      />

      {/* Smart Error Correction Dialog */}
      {activeCorrection && CorrectionDialog && (
        <CorrectionDialog
          workOrder={activeCorrection.workOrder}
          onSuccess={onCorrectionSuccess}
          onCancel={clearCorrection}
        />
      )}

      {/* Diálogo de Inicio de Producción */}
      {selectedOrderForNfc && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Iniciar Producción</h2>
            <p className="mb-2">Orden: {selectedOrderForNfc.order_number}</p>
            <p className="mb-4">Producto: {selectedOrderForNfc.product_name}</p>
            <div className="flex gap-2">
              <Button onClick={() => {
                startProductionDirectly(selectedOrderForNfc);
                setSelectedOrderForNfc(null);
              }}>
                Iniciar
              </Button>
              <Button variant="outline" onClick={() => setSelectedOrderForNfc(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
